import { Add, Close, Delete, Edit, Save } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Popper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useCreateServiceSubcategory, useGetShopServices } from '../../../../services/business/services';
import { SHOP_SERVICE_PRICE_TYPES } from '../../../../utility/Constant';
import { formatCurrency } from '../../../../utility/helpers/displayHelper';
import ServiceBlueSvg from '../../../../utility/Svg/ServiceBlueSvg';

const ServicesSection = ({ serviceList, setServiceList }) => {
    // Use TanStack Query for service data
    const {
        data: servicesData,
        isLoading: servicesLoading,
        error: servicesError,
        refetch: refetchServices,
    } = useGetShopServices({
        pagination: false,
        limit: 2500,
        sortBy: 'name',
        sortType: 'ASC',
    });

    // Use mutation for creating service subcategory
    const createServiceMutation = useCreateServiceSubcategory({
        onSuccess: (data) => {
            refetchServices();
        },
        onError: (error) => {
            console.error('Error creating service:', error);
            toast.error('Failed to create service');
        },
    });

    const cachedServices = servicesData?.data || [];
    const [serviceFilteredData, setServiceFilteredData] = useState(cachedServices);
    const [anchorEls, setAnchorEls] = useState(Array(serviceList.length).fill(false));
    const [serviceLoader, setServiceLoader] = useState(false);
    const inputFielRef = useRef([]);
    const textFieldRef = useRef([]);
    const inputDivRef = useRef([]);
    const preventCloseRef = useRef(false);
    const previousServiceRef = useRef([]); // Store previous service data when editing

    // Update filtered data when services data changes
    useEffect(() => {
        if (cachedServices.length > 0) {
            setServiceFilteredData(cachedServices);
        }
    }, [cachedServices]);

    // Update anchorEls when serviceList length changes
    useEffect(() => {
        setAnchorEls(Array(serviceList.length).fill(false));
    }, [serviceList.length]);

    const handleServiceChange = (value, index) => {
        setServiceList((prevList) => prevList.map((service, i) => (i === index ? { ...service, serviceShowName: value } : service)));
        if (!value) {
            let cloneService = [...serviceList];
            cloneService[index] = { _id: 'select' };
            setServiceList(cloneService);
            setServiceFilteredData(cachedServices);
        } else {
            const filtered = cachedServices.filter((item) => item.serviceSubCategory.name.toLowerCase().includes(value.toLowerCase()));
            const exactMatch = filtered.some((item) => item.serviceSubCategory.name.toLowerCase() === value.toLowerCase());
            const updatedFilteredData = exactMatch ? filtered : [{ _id: 'new', serviceSubCategory: { name: value } }, ...filtered];
            setServiceFilteredData(updatedFilteredData);
        }
    };

    // Restore previous service if user cancels editing
    const handleCancelEdit = (outerIndex) => {
        if (previousServiceRef.current[outerIndex]) {
            const currentInputValue = serviceList[outerIndex]?.serviceShowName || '';
            const previousServiceName = previousServiceRef.current[outerIndex]?.serviceShowName || '';

            // Only restore if the current input matches the previous service name
            // If user has changed the text, don't restore - keep their changes
            if (currentInputValue.trim().toLowerCase() === previousServiceName.trim().toLowerCase()) {
                setServiceList((prevList) => {
                    const updatedList = [...prevList];
                    updatedList[outerIndex] = previousServiceRef.current[outerIndex];
                    return updatedList;
                });
            }
            // Clear the previous service ref regardless
            previousServiceRef.current[outerIndex] = null;
        }
        // Close dropdown
        setAnchorEls((prev) => {
            const newAnchors = [...prev];
            newAnchors[outerIndex] = false;
            return newAnchors;
        });
    };

    const handleOnFocus = (event, outerIndex) => {
        const value = event?.currentTarget?.value?.trim();
        if (value) {
            const filtered = cachedServices.filter((item) => item.serviceSubCategory.name.toLowerCase().includes(value?.toLowerCase()));
            const exactMatch = filtered.some((item) => item.serviceSubCategory.name.toLowerCase() === value?.toLowerCase());
            const updatedFilteredData = exactMatch ? filtered : [{ _id: 'new', serviceSubCategory: { name: value } }, ...filtered];
            setServiceFilteredData(updatedFilteredData);
        } else {
            setServiceFilteredData(cachedServices);
        }
    };

    const handleAddNewService = () => {
        let cloneService = [...serviceList];
        cloneService.push({ _id: 'select', showDescription: false });
        setServiceList(cloneService);

        setTimeout(() => {
            const inputField = document.getElementById(`service-search-autocomplete-${cloneService.length - 1}`);
            if (inputField) {
                inputField.focus();

                // Trigger dropdown by simulating ArrowDown key press
                setTimeout(() => {
                    const event = new KeyboardEvent('keydown', {
                        key: 'ArrowDown',
                        code: 'ArrowDown',
                        keyCode: 40,
                        which: 40,
                        bubbles: true,
                        cancelable: true,
                    });
                    inputField.dispatchEvent(event);
                }, 200);
            }
        }, 300);
    };

    const handleSaveService = async (serviceName, outerIndex) => {
        preventCloseRef.current = true;
        let payload = {
            name: serviceName,
        };

        setServiceLoader(true);
        try {
            await createServiceMutation.mutateAsync(payload);
            // Refetch services to get the newly created one
            const { data: updatedServicesData } = await refetchServices();
            const updatedServices = updatedServicesData?.data || [];

            // Find newly created service from updated list
            const newService = updatedServices.find((item) => item.serviceSubCategory.name.toLowerCase() === serviceName.toLowerCase());

            if (newService) {
                // Update service list at the correct index
                setServiceList((prevList) => {
                    const updatedList = [...prevList];
                    updatedList[outerIndex] = {
                        ...newService,
                        serviceShowName: newService.serviceSubCategory.name,
                        showDescription: false,
                    };
                    return updatedList;
                });

                // Close the dropdown
                const newAnchorEls = [...anchorEls];
                newAnchorEls[outerIndex] = false;
                setAnchorEls(newAnchorEls);

                setTimeout(() => {
                    inputFielRef.current[outerIndex]?.blur({ preventDefault: () => {} });
                }, 300);
            }
        } catch (error) {
            console.error('Error creating service:', error);
            toast.error('Failed to create service');
        } finally {
            setServiceLoader(false);
            setTimeout(() => {
                preventCloseRef.current = false;
            }, 300);
        }
    };

    const handleCreateOrSelectService = (selectedItem, outerIndex) => {
        preventCloseRef.current = true;
        const value = selectedItem?.serviceSubCategory?.name;

        // Check for duplicate service name with a different index
        const isDuplicate = serviceList.some((service, index) => service.serviceShowName === value && index !== outerIndex);

        if (isDuplicate) {
            toast.error(`A service with the same name already exists.`);
            preventCloseRef.current = false;
            // Keep dropdown open
            setAnchorEls((prev) => {
                const newAnchors = [...prev];
                newAnchors[outerIndex] = true;
                return newAnchors;
            });
            return;
        }

        let cloneService = [...serviceList];
        // Preserve description and showDescription from current service
        let storeDescription = cloneService[outerIndex]?.description;
        let storeShowDescription = cloneService[outerIndex]?.showDescription ?? false;
        cloneService[outerIndex] = selectedItem;
        cloneService[outerIndex].description = storeDescription;
        cloneService[outerIndex].showDescription = storeShowDescription;
        cloneService[outerIndex].serviceShowName = value;
        setServiceList(cloneService);

        // Clear previous service ref since we've successfully selected
        previousServiceRef.current[outerIndex] = null;

        // Close the dropdown
        const newAnchorEls = [...anchorEls];
        newAnchorEls[outerIndex] = false;
        setAnchorEls(newAnchorEls);

        setTimeout(() => {
            inputFielRef.current[outerIndex]?.blur({ preventDefault: () => {} });
        }, 300);
    };

    const handleServiceSelection = (item, outerIndex) => {
        handleCreateOrSelectService(item, outerIndex);
    };

    return (
        <>
            <div className="customBoxWrapper" style={{ paddingLeft: 0, paddingRight: 0 }}>
                {/* ServiceHeader */}
                <Grid container alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 3, px: 3 }}>
                    <Grid>
                        <div className="accordianTitleWrapper">
                            <ServiceBlueSvg />
                            <Typography className="accordianTitle">Services</Typography>
                        </div>
                    </Grid>
                    {serviceList.length <= 0 && (
                        <Grid sm="auto">
                            <Button
                                startIcon={<Add />}
                                color="secondary"
                                variant={'contained'}
                                className="btn-text-size-manage"
                                style={{ height: '42px' }}
                                onClick={handleAddNewService}
                                disabled={serviceList[serviceList?.length - 1]?._id === 'select'}
                            >
                                ADD SERVICE
                            </Button>
                        </Grid>
                    )}
                </Grid>

                {/* Services Table */}
                <TableContainer>
                    <Table aria-label="service table">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        minWidth: '40px',
                                        width: '40px',
                                        height: '40px',
                                        maxWidth: '120px',
                                        padding: '4px 16px',
                                        color: '#212121',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderBottom: 'none',
                                        background: '#F8F8F8',
                                    }}
                                >
                                    No.
                                </TableCell>
                                <TableCell
                                    style={{
                                        maxWidth: '65.01%',
                                        width: 'auto',
                                        minWidth: '250px',
                                        height: '40px',
                                        padding: '4px 16px',
                                        color: '#212121',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderBottom: 'none',
                                        background: '#F8F8F8',
                                    }}
                                >
                                    Service
                                </TableCell>
                                <TableCell
                                    style={{
                                        minWidth: '80px',
                                        maxWidth: '20%',
                                        width: '20%',
                                        height: '40px',
                                        padding: '4px 16px',
                                        color: '#212121',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderBottom: 'none',
                                        background: '#F8F8F8',
                                    }}
                                    align="left"
                                >
                                    Price
                                </TableCell>
                                <TableCell
                                    style={{
                                        minWidth: '120px',
                                        width: '12%',
                                        height: '40px',
                                        padding: '4px 16px',
                                        color: '#212121',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        borderBottom: 'none',
                                        background: '#F8F8F8',
                                    }}
                                    align="right"
                                ></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {serviceList &&
                                serviceList?.length > 0 &&
                                serviceList.map((outerItem, outerIndex) => (
                                    <TableRow key={outerIndex}>
                                        <TableCell
                                            component="th"
                                            align="left"
                                            scope="row"
                                            sx={{ padding: '8px 16px!important' }}
                                            style={{
                                                maxWidth: '120px',
                                                minWidth: '40px',
                                                width: '40px',
                                            }}
                                        >
                                            {outerIndex + 1}
                                        </TableCell>

                                        <TableCell
                                            align="left"
                                            sx={{
                                                padding: '8px 12px !important',
                                                width: outerItem?._id === 'select' ? 'auto' : undefined,
                                            }}
                                            colSpan={outerItem?._id === 'select' ? 2 : 1}
                                            style={{
                                                maxWidth: outerItem?._id === 'select' ? 'none' : '65.01%',
                                                width: outerItem?._id === 'select' ? 'auto' : '65.01%',
                                                minWidth: outerItem?._id === 'select' ? 'auto' : '300px',
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                {/* Show Autocomplete only if service is not selected */}
                                                {outerItem?._id === 'select' ? (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            minWidth: '200px',
                                                        }}
                                                        ref={(el) => (inputDivRef.current[outerIndex] = el)}
                                                    >
                                                        <Autocomplete
                                                            freeSolo
                                                            id={`service-search-autocomplete-${outerIndex}`}
                                                            disabled={servicesLoading}
                                                            clearIcon={null}
                                                            options={serviceFilteredData}
                                                            filterOptions={(x) => x}
                                                            open={anchorEls[outerIndex]}
                                                            onFocus={(event) => {
                                                                if (cachedServices?.length > 0) {
                                                                    handleOnFocus(event, outerIndex);
                                                                    setAnchorEls((prev) => {
                                                                        const newAnchors = prev.map(() => false);
                                                                        newAnchors[outerIndex] = true;
                                                                        return newAnchors;
                                                                    });
                                                                }
                                                            }}
                                                            onBlur={(event) => {
                                                                setTimeout(() => {
                                                                    if (preventCloseRef.current) {
                                                                        if (previousServiceRef.current[outerIndex] && outerItem?._id === 'select') {
                                                                            handleCancelEdit(outerIndex);
                                                                        }
                                                                        setAnchorEls((prev) => {
                                                                            const newAnchors = [...prev];
                                                                            newAnchors[outerIndex] = false;
                                                                            return newAnchors;
                                                                        });
                                                                        return;
                                                                    }
                                                                    // Check if focus moved outside the input div
                                                                    if (!inputDivRef.current[outerIndex]?.contains(document.activeElement)) {
                                                                        // If user didn't select anything and we have a previous service, restore it
                                                                        if (previousServiceRef.current[outerIndex] && outerItem?._id === 'select') {
                                                                            handleCancelEdit(outerIndex);
                                                                        } else {
                                                                            // Close dropdown when clicking outside
                                                                            setAnchorEls((prev) => {
                                                                                const newAnchors = [...prev];
                                                                                newAnchors[outerIndex] = false;
                                                                                return newAnchors;
                                                                            });
                                                                        }
                                                                    }
                                                                }, 200);
                                                            }}
                                                            value={
                                                                // When editing we intentionally keep `value` null so
                                                                // Autocomplete doesn't override the user's typed text
                                                                // with the last selected option label.
                                                                outerItem?._id === 'select'
                                                                    ? null
                                                                    : outerItem?.serviceSubCategory?._id
                                                                      ? (serviceFilteredData?.length > 0 &&
                                                                            serviceFilteredData?.find(
                                                                                (opt) => opt?.serviceSubCategory?._id === outerItem?.serviceSubCategory?._id
                                                                            )) ||
                                                                        (outerItem?.serviceSubCategory && {
                                                                            _id: outerItem?.serviceSubCategory?._id,
                                                                            serviceSubCategory: outerItem?.serviceSubCategory,
                                                                            serviceShowName: outerItem?.serviceShowName,
                                                                            priceType: outerItem?.priceType,
                                                                            priceMin: outerItem?.priceMin,
                                                                            priceMax: outerItem?.priceMax,
                                                                            fixedPrice: outerItem?.fixedPrice,
                                                                        })
                                                                      : null
                                                            }
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();

                                                                    const highlightedOption = document.querySelector('li.MuiAutocomplete-option.Mui-focused');
                                                                    const optionText = highlightedOption?.textContent?.trim()?.toLowerCase();

                                                                    const filteredOptions = serviceFilteredData.filter((option) =>
                                                                        option?.serviceSubCategory?.name
                                                                            .toLowerCase()
                                                                            .includes(optionText || outerItem?.serviceShowName?.toLowerCase() || '')
                                                                    );

                                                                    const exactMatch = serviceFilteredData.find(
                                                                        (option) =>
                                                                            option?._id !== 'new' &&
                                                                            option?.serviceSubCategory?.name?.toLowerCase() ===
                                                                                (optionText || outerItem?.serviceShowName?.toLowerCase())
                                                                    );

                                                                    if (exactMatch) {
                                                                        handleServiceSelection(exactMatch, outerIndex);
                                                                    } else if (outerItem?.serviceShowName) {
                                                                        handleSaveService(outerItem?.serviceShowName, outerIndex);
                                                                    } else if (filteredOptions.length > 0) {
                                                                        const firstOption = filteredOptions[0];
                                                                        handleServiceSelection(firstOption, outerIndex);
                                                                        setTimeout(() => document.activeElement.blur(), 0);
                                                                    }
                                                                }
                                                            }}
                                                            inputValue={outerItem?.serviceShowName ?? ''}
                                                            getOptionLabel={(option) =>
                                                                typeof option === 'string' ? option : option?.serviceSubCategory?.name || ''
                                                            }
                                                            onInputChange={(event, newInputValue, reason) => {
                                                                if (cachedServices?.length > 0) {
                                                                    handleServiceChange(newInputValue, outerIndex);
                                                                }
                                                            }}
                                                            onChange={(event, newValue) => {
                                                                if (!newValue) {
                                                                    handleServiceChange('', outerIndex);
                                                                } else if (typeof newValue === 'string') {
                                                                    // Handle string input
                                                                } else {
                                                                    // Check if this is the same service that's already selected
                                                                    const currentServiceId = outerItem?.serviceSubCategory?._id;
                                                                    const selectedServiceId = newValue?.serviceSubCategory?._id;

                                                                    if (currentServiceId === selectedServiceId && outerItem?._id !== 'select') {
                                                                        // Same service already selected, just close dropdown
                                                                        setAnchorEls((prev) => {
                                                                            const newAnchors = [...prev];
                                                                            newAnchors[outerIndex] = false;
                                                                            return newAnchors;
                                                                        });
                                                                        // Clear previous service ref since we're keeping the same service
                                                                        previousServiceRef.current[outerIndex] = null;
                                                                    } else {
                                                                        // Different service or in select state, proceed with selection
                                                                        handleServiceSelection(newValue, outerIndex);
                                                                    }
                                                                }
                                                            }}
                                                            renderOption={(props, option) => {
                                                                if (option._id === 'new')
                                                                    return (
                                                                        <li
                                                                            {...props}
                                                                            key={option?.serviceSubCategory?._id || option._id || `temp-${Math.random()}`}
                                                                            style={{
                                                                                height: '44px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                cursor: 'default',
                                                                                pointerEvents: 'none',
                                                                                paddingRight: serviceLoader ? '12px' : '0',
                                                                            }}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                            }}
                                                                        >
                                                                            <span> {option.serviceSubCategory.name}</span>
                                                                            {serviceLoader ? (
                                                                                <CircularProgress size={20} sx={{ color: '#006CDD' }} />
                                                                            ) : (
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        justifyContent: 'center',
                                                                                        padding: '6px 16px',
                                                                                        background: 'rgba(33, 150, 243, 0.15)',
                                                                                        cursor: 'pointer',
                                                                                        pointerEvents: 'all',
                                                                                    }}
                                                                                    onClick={(event) => {
                                                                                        event.preventDefault();
                                                                                        event.stopPropagation();
                                                                                        handleSaveService(option.serviceSubCategory.name, outerIndex);
                                                                                    }}
                                                                                >
                                                                                    <Save
                                                                                        sx={{
                                                                                            color: '#006CDD',
                                                                                            width: '26px',
                                                                                            height: '26px',
                                                                                        }}
                                                                                    />
                                                                                    <span
                                                                                        style={{
                                                                                            textDecoration: 'underline',
                                                                                            color: '#006CDD',
                                                                                            fontSize: '13px',
                                                                                            textTransform: 'uppercase',
                                                                                            fontWeight: 500,
                                                                                            lineHeight: '26px',
                                                                                            letterSpacing: '0.46px',
                                                                                            marginLeft: '8px',
                                                                                        }}
                                                                                    >
                                                                                        Save
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </li>
                                                                    );
                                                                if (option?.serviceSubCategory?.name) {
                                                                    return (
                                                                        <li
                                                                            {...props}
                                                                            key={option?.serviceSubCategory?._id || option._id || `temp-${Math.random()}`}
                                                                            style={{
                                                                                height: '44px',
                                                                                paddingLeft: '16px',
                                                                                paddingRight: '16px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                            }}
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    fontSize: '16px',
                                                                                    // fontWeight: 600,
                                                                                    // color: '#212121',
                                                                                    // flex: 1,
                                                                                    width: '100%',
                                                                                }}
                                                                            >
                                                                                {option?.serviceSubCategory?.name}
                                                                            </span>
                                                                            {/* {option?.priceType === SHOP_SERVICE_PRICE_TYPES.RANGE ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        marginLeft: '16px',
                                                                                        whiteSpace: 'nowrap',
                                                                                        width: '25%',
                                                                                        textAlign: 'left',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.60)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 400,
                                                                                        }}
                                                                                    >
                                                                                        Range:{' '}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        {formatCurrency(option?.priceMin)} - {formatCurrency(option?.priceMax)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : option?.priceType === SHOP_SERVICE_PRICE_TYPES.FIXED ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        marginLeft: '16px',
                                                                                        whiteSpace: 'nowrap',
                                                                                        width: '25%',
                                                                                        textAlign: 'left',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.60)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 400,
                                                                                        }}
                                                                                    >
                                                                                        Fixed:{' '}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        {formatCurrency(option?.fixedPrice)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : option?.priceType === SHOP_SERVICE_PRICE_TYPES.STARTING_FROM ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        marginLeft: '16px',
                                                                                        whiteSpace: 'nowrap',
                                                                                        width: '25%',
                                                                                        textAlign: 'left',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.60)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 400,
                                                                                        }}
                                                                                    >
                                                                                        From:{' '}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        component="span"
                                                                                        sx={{
                                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        {formatCurrency(option?.priceMin)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : null} */}
                                                                        </li>
                                                                    );
                                                                } else {
                                                                    return null;
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder="Search or add service name..."
                                                                    inputRef={(el) => (inputFielRef.current[outerIndex] = el)}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        disableUnderline: true,
                                                                    }}
                                                                    sx={{
                                                                        '& .MuiInputBase-root': {
                                                                            borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                                                        },
                                                                        '& .MuiInputBase-root:hover': {
                                                                            borderBottom: '1px solid rgba(0, 0, 0, 0.87)',
                                                                        },
                                                                        '& .MuiInputBase-root.Mui-focused': {
                                                                            borderBottom: '2px solid #1976d2',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            ListboxProps={{
                                                                style: { maxHeight: '190px' },
                                                            }}
                                                            PopperComponent={(props) => (
                                                                <Popper
                                                                    {...props}
                                                                    sx={{
                                                                        '& .MuiAutocomplete-paper': {
                                                                            borderRadius: '4px!important',
                                                                            boxShadow:
                                                                                '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
                                                                        },
                                                                        width: '100%',
                                                                        maxWidth: '672px',
                                                                        zIndex: 1300,
                                                                        borderRadius: '4px!important',
                                                                        boxShadow:
                                                                            '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)!important',
                                                                    }}
                                                                    modifiers={[
                                                                        {
                                                                            name: 'offset',
                                                                            options: {
                                                                                offset: [0, 14],
                                                                            },
                                                                        },
                                                                        {
                                                                            name: 'preventOverflow',
                                                                            options: {
                                                                                padding: 8,
                                                                            },
                                                                        },
                                                                        {
                                                                            name: 'flip',
                                                                            enabled: false,
                                                                        },
                                                                    ]}
                                                                    placement="bottom-start"
                                                                />
                                                            )}
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    width: '100%',
                                                                    backgroundColor: 'transparent',
                                                                    padding: '0',
                                                                    borderRadius: '0!important',
                                                                    height: 'auto',
                                                                },
                                                                '& fieldset': {
                                                                    border: 'none',
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    // Show Service Name with Icon when selected
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                                                        <div style={{ width: '18px', height: '18px' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                                <path
                                                                    d="M11.026 4.72505C10.8886 4.86524 10.8116 5.05373 10.8116 5.25005C10.8116 5.44636 10.8886 5.63485 11.026 5.77505L12.226 6.97505C12.3662 7.11247 12.5547 7.18944 12.751 7.18944C12.9473 7.18944 13.1358 7.11247 13.276 6.97505L16.1035 4.14755C16.4806 4.98094 16.5948 5.90947 16.4308 6.80941C16.2669 7.70934 15.8325 8.53793 15.1857 9.18476C14.5389 9.83158 13.7103 10.2659 12.8104 10.4299C11.9104 10.5939 10.9819 10.4797 10.1485 10.1025L4.96599 15.285C4.66762 15.5834 4.26295 15.751 3.84099 15.751C3.41903 15.751 3.01436 15.5834 2.71599 15.285C2.41762 14.9867 2.25 14.582 2.25 14.16C2.25 13.7381 2.41762 13.3334 2.71599 13.035L7.89849 7.85255C7.52136 7.01916 7.40718 6.09062 7.57115 5.19069C7.73512 4.29075 8.16946 3.46216 8.81628 2.81534C9.46311 2.16851 10.2917 1.73417 11.1916 1.5702C12.0916 1.40623 13.0201 1.52042 13.8535 1.89755L11.0335 4.71755L11.026 4.72505Z"
                                                                    fill="#006CDD"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '16px',
                                                                lineHeight: '24px',
                                                                letterSpacing: '0.15px',
                                                                color: '#212121',
                                                                marginTop: 0,
                                                            }}
                                                        >
                                                            {outerItem?.serviceShowName || outerItem?.serviceSubCategory?.name}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Description Field - Only show if service is selected and showDescription is true */}
                                                {outerItem?.serviceSubCategory?._id && outerItem?._id !== 'select' && outerItem?.showDescription && (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            gap: '4px',
                                                            alignItems: 'center',
                                                            justifyContent: !outerItem?.description ? 'flex-start' : 'space-between',
                                                            marginTop: '0px',
                                                        }}
                                                    >
                                                        <TextField
                                                            inputRef={(el) => (textFieldRef.current[outerIndex] = el)}
                                                            multiline
                                                            maxRows={5}
                                                            variant="standard"
                                                            value={outerItem?.description || ''}
                                                            onChange={(event) => {
                                                                setServiceList((prevList) =>
                                                                    prevList.map((service, i) =>
                                                                        i === outerIndex
                                                                            ? {
                                                                                  ...service,
                                                                                  description: event.target.value,
                                                                              }
                                                                            : service
                                                                    )
                                                                );
                                                            }}
                                                            fullWidth={outerItem?.description && outerItem?.description !== '' ? true : false}
                                                            placeholder="Add optional description"
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                style: {
                                                                    width: !outerItem?.description ? '178px' : '100%',
                                                                    fontSize: '16px',
                                                                    color: '#646466',
                                                                    cursor: 'text',
                                                                    lineHeight: '143%',
                                                                    letterSpacing: ' 0.17px',
                                                                },
                                                            }}
                                                            sx={{
                                                                padding: 0,
                                                                border: 'none',
                                                                fontSize: '16px',
                                                                '& .MuiInputBase-input': {
                                                                    padding: 0,
                                                                },
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    border: 'none',
                                                                },
                                                            }}
                                                        />
                                                        {/* <Edit
                                                            sx={{
                                                                color: '#646466',
                                                                fontSize: '20px !important',
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => {
                                                                if (textFieldRef.current[outerIndex]) {
                                                                    textFieldRef.current[outerIndex].focus();
                                                                }
                                                            }}
                                                        /> */}
                                                    </div>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {/* Price column - only show when service is selected */}
                                        {outerItem?._id !== 'select' && (
                                            <TableCell align="start" sx={{ padding: '8px 12px !important' }}>
                                                {outerItem?.priceType === SHOP_SERVICE_PRICE_TYPES.RANGE ? (
                                                    <Stack spacing={0}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                color: 'rgba(0, 0, 0, 0.60)',
                                                                lineHeight: '143%',
                                                                letterSpacing: '0.17px',
                                                                marginBottom: 0,
                                                            }}
                                                        >
                                                            Range
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                lineHeight: '143%',
                                                                letterSpacing: '0.17px',
                                                                marginTop: 0,
                                                            }}
                                                        >
                                                            {formatCurrency(outerItem?.priceMin)} - {formatCurrency(outerItem?.priceMax)}
                                                        </Typography>
                                                    </Stack>
                                                ) : outerItem?.priceType === SHOP_SERVICE_PRICE_TYPES.FIXED ? (
                                                    <Stack spacing={0}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                lineHeight: '143%',
                                                                letterSpacing: '0.17px',
                                                                marginTop: 0,
                                                            }}
                                                        >
                                                            {formatCurrency(outerItem?.fixedPrice)}
                                                        </Typography>
                                                    </Stack>
                                                ) : outerItem?.priceType === SHOP_SERVICE_PRICE_TYPES.STARTING_FROM ? (
                                                    <Stack spacing={0}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                color: 'rgba(0, 0, 0, 0.60)',
                                                                lineHeight: '143%',
                                                                letterSpacing: '0.17px',
                                                                marginBottom: 0,
                                                            }}
                                                        >
                                                            From
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                lineHeight: '143%',
                                                                letterSpacing: '0.17px',
                                                                marginTop: 0,
                                                            }}
                                                        >
                                                            {formatCurrency(outerItem?.priceMin)}
                                                        </Typography>
                                                    </Stack>
                                                ) : (
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            lineHeight: '143%',
                                                            letterSpacing: '0.17px',
                                                        }}
                                                    >
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        )}

                                        <TableCell align="right" sx={{ padding: '8px 16px !important' }}>
                                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                                {/* Show Edit icon only when service is selected */}
                                                {outerItem?._id !== 'select' && outerItem?.serviceSubCategory?._id && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(event) => {
                                                            // Store the current service as previous before editing
                                                            previousServiceRef.current[outerIndex] = { ...outerItem };

                                                            // Reset to select state to show autocomplete again
                                                            let cloneService = [...serviceList];
                                                            cloneService[outerIndex] = {
                                                                _id: 'select',
                                                                serviceShowName: outerItem?.serviceShowName || '',
                                                                serviceSubCategory: outerItem?.serviceSubCategory,
                                                                description: outerItem?.description, // Preserve description
                                                                priceType: outerItem?.priceType,
                                                                priceMin: outerItem?.priceMin,
                                                                priceMax: outerItem?.priceMax,
                                                                fixedPrice: outerItem?.fixedPrice,
                                                            };
                                                            setServiceList(cloneService);

                                                            // Open the dropdown
                                                            setAnchorEls((prev) => {
                                                                const newAnchors = prev.map(() => false);
                                                                newAnchors[outerIndex] = true;
                                                                return newAnchors;
                                                            });

                                                            // Update filtered data to include current service
                                                            if (outerItem?.serviceSubCategory) {
                                                                const currentServiceInList = cachedServices.find(
                                                                    (item) => item.serviceSubCategory._id === outerItem.serviceSubCategory._id
                                                                );
                                                                if (currentServiceInList) {
                                                                    setServiceFilteredData(cachedServices);
                                                                }
                                                            }

                                                            setTimeout(() => {
                                                                if (inputFielRef.current[outerIndex]) {
                                                                    const inputElement = inputFielRef.current[outerIndex];
                                                                    inputElement.focus();
                                                                }
                                                            }, 100);
                                                        }}
                                                        sx={{ color: '#646466', padding: '4px' }}
                                                    >
                                                        <Edit sx={{ fontSize: '20px!important' }} />
                                                    </IconButton>
                                                )}

                                                {/* Toggle Description Button */}
                                                {outerItem?._id !== 'select' && outerItem?.serviceSubCategory?._id && (
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        startIcon={
                                                            outerItem?.showDescription ? (
                                                                <Close sx={{ fontSize: '20px!important' }} />
                                                            ) : (
                                                                <Add sx={{ fontSize: '20px!important' }} />
                                                            )
                                                        }
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setServiceList((prevList) =>
                                                                prevList.map((service, i) =>
                                                                    i === outerIndex
                                                                        ? {
                                                                              ...service,
                                                                              showDescription: !service.showDescription,
                                                                          }
                                                                        : service
                                                                )
                                                            );

                                                            // Auto-focus description field when showing it
                                                            if (!outerItem?.showDescription) {
                                                                setTimeout(() => {
                                                                    if (textFieldRef.current[outerIndex]) {
                                                                        textFieldRef.current[outerIndex].focus();
                                                                    }
                                                                }, 100);
                                                            }
                                                        }}
                                                        sx={{
                                                            color: '#646466',
                                                            padding: '0 4px!important',
                                                            minWidth: 'auto',
                                                            fontSize: '16px',
                                                            fontWeight: 500,
                                                            textTransform: 'none',
                                                            '& .MuiButton-startIcon': {
                                                                marginRight: '2px',
                                                                marginLeft: 0,
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        DESC
                                                    </Button>
                                                )}
                                                {/* Always show Delete icon */}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (outerItem?._id === 'select') {
                                                            setServiceList((prevList) => {
                                                                const updatedList = [...prevList];
                                                                updatedList.splice(outerIndex, 1);
                                                                return updatedList;
                                                            });

                                                            // Remove from previousServiceRef
                                                            if (previousServiceRef.current) {
                                                                previousServiceRef.current.splice(outerIndex, 1);
                                                            }

                                                            // Remove from anchorEls
                                                            setAnchorEls((prev) => {
                                                                const newAnchors = [...prev];
                                                                newAnchors.splice(outerIndex, 1);
                                                                return newAnchors;
                                                            });
                                                        } else {
                                                            setServiceList((prevList) => {
                                                                const updatedList = [...prevList];
                                                                updatedList[outerIndex] = { _id: 'select' };
                                                                return updatedList;
                                                            });

                                                            // Clear previous service ref
                                                            previousServiceRef.current[outerIndex] = null;

                                                            // Open dropdown (optional, but good for UX if we want them to search immediately)
                                                            setAnchorEls((prev) => {
                                                                const newAnchors = [...prev];
                                                                newAnchors[outerIndex] = true;
                                                                return newAnchors;
                                                            });

                                                            setTimeout(() => {
                                                                const inputField = document.getElementById(`service-search-autocomplete-${outerIndex}`);
                                                                if (inputField) {
                                                                    inputField.focus();
                                                                }
                                                            }, 100);
                                                        }
                                                    }}
                                                    sx={{ color: '#d32f2f', padding: '4px' }}
                                                >
                                                    <Delete sx={{ fontSize: '20px!important' }} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            {serviceList.length > 0 && (
                <Stack mt={'24px'} direction={'row'} justifyContent={'center'} alignItems={'center'}>
                    <Button
                        startIcon={<Add />}
                        color="secondary"
                        variant={'contained'}
                        className="btn-text-size-manage"
                        style={{ height: '42px' }}
                        onClick={handleAddNewService}
                        disabled={serviceList[serviceList?.length - 1]?._id === 'select'}
                    >
                        ADD SERVICE
                    </Button>
                </Stack>
            )}
        </>
    );
};

export default ServicesSection;
